import type { ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { TableCell } from "@/components/ui/table";
import { cn } from "@/lib/utils";

/**
 * Bunka ID ako strom (KOLO 136) — zostava `A`, pod ňou ├─ `A1` … └─ `A4`,
 * pod sendvičom o úroveň hlbšie `A3a`, `A3b`.
 *
 * User so screenshotom DaexCUT: *„čo keby tak graficky pridáš to A1…
 * ako keby strom ako v Daex, ale lepšie"*. Daex kreslí strom v samostatnom
 * paneli znakmi; tu sú čiary tenké spany cez **celú výšku bunky**, takže
 * sa medzi riadkami tabuľky napoja do súvislej vetvy, a rozbaľovanie
 * (zostava, sendvič) sedí priamo na vetve, nie v osobitnom stĺpci.
 *
 * Geometria v px od ľavého okraja bunky: chevron zostavy 4–20 (os 12 =
 * kmeň dielcov), chevron dielca 24–40 (os 32 = kmeň vrstiev), kód vrstvy
 * od 44. Bunka má `relative`, čiary sú `absolute` voči nej.
 *
 * KOLO 180: čiary majú vlastnú farbu `tab-strom` a 1,5 px (dovtedy
 * farba textu s `opacity-40` — vnorené riadky „veľmi splývali"),
 * v tmavom páse zostavy ostávajú svetlé. Zbalený uzol má šípku
 * v rámiku, aby bolo vidno, že pod ním niečo je.
 */

const OS_1 = 12;
const OS_2 = 32;
const CHEVRON_1 = 24;
const OBSAH_2 = 44;

export type HlbkaStromu = 0 | 1 | 2;

interface Props {
  /** 0 zostava, 1 dielec / paušál, 2 vrstva / vnorená operácia */
  hlbka: HlbkaStromu;
  /** Posledný medzi súrodencami — vetva končí └─ */
  posledny?: boolean;
  /** Pri hĺbke 2: rodič je posledný v zostave → kmeň zostavy tu už nejde */
  rodicPosledny?: boolean;
  /** Má deti, ktoré sa dajú rozbaliť (zostava, sendvič, operácie) */
  rozbalitelne?: boolean;
  rozbalene?: boolean;
  /** Má rozbalený uzol pod sebou riadky? Prázdna zostava vetvu dole nepustí. */
  deti?: boolean;
  onToggle?: () => void;
  toggleLabel?: string;
  toggleTitle?: string;
  className?: string;
  title?: string;
  children?: ReactNode;
}

/** Farba čiar: v páse zostavy (tmavý) svetlá, inde `tab-strom`. */
function farbaCiary(vPase: boolean): string {
  return vPase ? "border-current opacity-50" : "border-tab-strom";
}

function Zvisla({
  x,
  od = "0",
  po = "0",
  nazov,
  vPase = false,
}: {
  x: number;
  od?: string;
  po?: string;
  nazov: string;
  vPase?: boolean;
}): JSX.Element {
  return (
    <span
      aria-hidden
      data-ciara={nazov}
      className={cn("pointer-events-none absolute border-l-[1.5px]", farbaCiary(vPase))}
      style={{ left: x, top: od, bottom: po }}
    />
  );
}

function Vodorovna({ od, po }: { od: number; po: number }): JSX.Element {
  return (
    <span
      aria-hidden
      data-ciara="vodorovna"
      className={cn("pointer-events-none absolute top-1/2 border-t-[1.5px]", farbaCiary(false))}
      style={{ left: od, width: po - od }}
    />
  );
}

export function StromBunka({
  hlbka,
  posledny = false,
  rodicPosledny = false,
  rozbalitelne = false,
  rozbalene = false,
  deti = true,
  onToggle,
  toggleLabel,
  toggleTitle,
  className,
  title,
  children,
}: Props): JSX.Element {
  const chevron = rozbalitelne ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
      aria-label={toggleLabel}
      title={toggleTitle}
      aria-expanded={rozbalene}
      className={cn(
        "relative z-10 flex h-4 w-4 shrink-0 items-center justify-center rounded hover:bg-background",
        // Zbalené: šípka v rámiku — „pod tým niečo je" (KOLO 180)
        !rozbalene &&
          (hlbka === 0
            ? "border border-current"
            : "border border-tab-strom bg-background"),
      )}
    >
      {rozbalene ? (
        <ChevronDown className="h-3.5 w-3.5" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" />
      )}
    </button>
  ) : null;

  // Kde začína obsah — kód sa v jednej úrovni zarovná, aj keď dielec
  // chevron nemá (slot pre chevron ostane prázdny a vetva doňho dobehne)
  const odsadenie = hlbka === 0 ? 4 : hlbka === 1 ? CHEVRON_1 : OBSAH_2;

  return (
    <TableCell
      // `max-w` + `truncate`: dlhý kód (staré zákazky z TopSolidu) nesmie
      // roztiahnuť tabuľku a s ňou celý editor (KOLO 96)
      className={cn("relative max-w-[120px] truncate pl-0 pr-1", className)}
      title={title}
      data-hlbka={hlbka}
      data-posledny={hlbka > 0 ? (posledny ? "1" : "0") : undefined}
    >
      {/* Kmeň úrovne 1 — pri dielci ├ alebo └, pri vrstve len │ pokračovanie */}
      {hlbka === 1 ? (
        <>
          <Zvisla x={OS_1} po={posledny ? "50%" : "0"} nazov="kmen-1" />
          <Vodorovna od={OS_1} po={rozbalitelne ? CHEVRON_1 : CHEVRON_1 + 12} />
        </>
      ) : null}
      {hlbka === 2 ? (
        <>
          {!rodicPosledny ? <Zvisla x={OS_1} nazov="kmen-1" /> : null}
          <Zvisla x={OS_2} po={posledny ? "50%" : "0"} nazov="kmen-2" />
          <Vodorovna od={OS_2} po={OBSAH_2 - 2} />
        </>
      ) : null}
      {/* Rozbalený uzol pustí vetvu dole k prvému dieťaťu */}
      {rozbalitelne && rozbalene && deti && hlbka < 2 ? (
        <Zvisla
          x={hlbka === 0 ? OS_1 : OS_2}
          od="50%"
          nazov="vetva-dole"
          vPase={hlbka === 0}
        />
      ) : null}
      <div className="flex min-w-0 items-center gap-0.5" style={{ paddingLeft: odsadenie }}>
        {chevron}
        {hlbka === 1 && !rozbalitelne ? <span className="w-4 shrink-0" /> : null}
        <span className="flex min-w-0 items-center truncate">{children}</span>
      </div>
    </TableCell>
  );
}
